import {
  PRICING_REPORT_CHART_PLOT_SELECTOR,
  PRICING_REPORT_FACET_PLOT_SELECTOR,
  PRICING_REPORT_PRINT_CHART_PLOT_HEIGHT_PX,
  PRICING_REPORT_PRINT_FACET_MINI_CHART_HEIGHT_PX,
} from "./pricingReportPrintDimensions";

function getChartPlotElement(chartInstance) {
  if (chartInstance?.isFacetGridComposite) {
    return chartInstance.getPlotElement?.() ?? null;
  }
  return chartInstance?.canvas?.closest(PRICING_REPORT_CHART_PLOT_SELECTOR) ?? null;
}

function resizeChartInstance(chartInstance) {
  if (chartInstance?.isFacetGridComposite) {
    if (typeof chartInstance.resize === "function") {
      chartInstance.resize();
    }
    return;
  }

  if (chartInstance && typeof chartInstance.resize === "function") {
    try {
      chartInstance.resize();
    } catch (_) {
      /* ignore resize errors */
    }
  }
}

function resizeFacetMiniPlots(plotElement, heightPx) {
  const facetPlots = plotElement?.querySelectorAll(PRICING_REPORT_FACET_PLOT_SELECTOR) ?? [];
  const restored = [];

  facetPlots.forEach((facetPlot) => {
    restored.push({
      facetPlot,
      previousInlineHeight: facetPlot.style.height,
    });
    facetPlot.style.height = `${heightPx}px`;
  });

  return restored;
}

/**
 * Temporarily enlarges on-screen chart plot containers for print PNG capture.
 * @param {Record<string, import('chart.js').Chart | undefined>} chartRefsByKey
 * @param {string[]} includedChartKeys
 * @returns {() => void} restore plot heights to the default screen size
 */
export function resizePricingReportChartsForPrint(chartRefsByKey, includedChartKeys) {
  const plotElementsToRestore = [];

  for (const chartKey of includedChartKeys) {
    const chartInstance = chartRefsByKey?.[chartKey];
    const plotElement = getChartPlotElement(chartInstance);
    if (!plotElement) continue;

    const facetPlotRestores = chartInstance?.isFacetGridComposite
      ? resizeFacetMiniPlots(plotElement, PRICING_REPORT_PRINT_FACET_MINI_CHART_HEIGHT_PX)
      : [];

    plotElementsToRestore.push({
      chartInstance,
      plotElement,
      previousInlineHeight: plotElement.style.height,
      facetPlotRestores,
    });

    if (!chartInstance?.isFacetGridComposite) {
      plotElement.style.height = `${PRICING_REPORT_PRINT_CHART_PLOT_HEIGHT_PX}px`;
    }

    resizeChartInstance(chartInstance);
  }

  return function restorePricingReportChartPlotHeights() {
    for (const {
      chartInstance,
      plotElement,
      previousInlineHeight,
      facetPlotRestores,
    } of plotElementsToRestore) {
      if (previousInlineHeight) {
        plotElement.style.height = previousInlineHeight;
      } else {
        plotElement.style.removeProperty("height");
      }

      for (const { facetPlot, previousInlineHeight: facetPreviousHeight } of facetPlotRestores) {
        if (facetPreviousHeight) {
          facetPlot.style.height = facetPreviousHeight;
        } else {
          facetPlot.style.removeProperty("height");
        }
      }

      resizeChartInstance(chartInstance);
    }
  };
}
